import React, { useCallback, useEffect, useState } from 'react';

import { Loader } from '@crehana/ui';
import { primaryLight } from '@crehana/ui/dist/styles/colors';

import CardLogos from 'Jsx/CheckoutV4/components/CardLogos';
import { useCheckoutContext } from 'Jsx/CheckoutV4/Context';
import { loadDlocalScript } from 'Jsx/CheckoutV4/utils/creditCard/loadPaymentMethodScripts';

interface MountedCardField {
  mount: (node: HTMLElement | null) => void;
}

type TokenCreator = (node: unknown, options: any) => any;

export interface DLocalData {
  createToken: TokenCreator;
  cardField: MountedCardField;
}

interface DLocalChange {
  complete: boolean;
  error?: { code: number; message: string };
}

interface DLocalProps {
  onFieldsMounted: (data: DLocalData) => void;
  onCreditCardChange: (isValid: boolean) => void;
  onIFramesLoaded: () => void;
}

const DLocal = ({
  onFieldsMounted,
  onCreditCardChange,
  onIFramesLoaded,
}: DLocalProps) => {
  const [isScriptLoading, setIsScriptLoading] = useState<boolean>(
    window.dlocal === undefined,
  );

  const [panError, setPanError] = useState<DLocalChange>();
  const [expirationError, setExpirationError] = useState<DLocalChange>();
  const [cvcError, setCvcError] = useState<DLocalChange>();

  const { countryPrefix } = useCheckoutContext();

  const createSmartFields = useCallback(() => {
    const dlocal = window.dlocal(window.DLOCAL_API_KEY);
    const fields = dlocal.fields({
      locale: countryPrefix === 'br' ? 'pt' : 'es',
      country: countryPrefix?.toUpperCase(),
    });

    const style = {
      base: {
        fontSize: '16px',
        lineHeight: '24px',
      },
      focus: {
        borderColor: primaryLight,
        border: '1px solid red',
      },
    };

    const cardNumberField = fields.create?.('pan', {
      style,
      placeholder: 'Número de tarjeta',
    });

    cardNumberField.addEventListener('change', setPanError);

    const expirationField = fields.create?.('expiration', {
      style,
      placeholder: 'MM/AA',
    });

    expirationField.addEventListener('change', setExpirationError);

    const cvvField = fields.create?.('cvv', { style });

    cvvField.addEventListener('change', setCvcError);

    cardNumberField.mount(document.getElementById('pan-field'));

    expirationField.mount(document.getElementById('expiration-field'));

    cvvField.mount(document.getElementById('cvv-field'));

    cardNumberField.addEventListener('ready', onIFramesLoaded);

    onFieldsMounted({
      createToken: dlocal.createToken,
      cardField: cardNumberField,
    });
  }, [countryPrefix, onFieldsMounted]);

  useEffect(() => {
    loadDlocalScript(() => {
      createSmartFields();
      setIsScriptLoading(false);
    });
  }, [createSmartFields]);

  useEffect(() => {
    const isPanValid = (panError && panError.complete) || false;
    const isExpirationValid =
      (expirationError && expirationError.complete) || false;
    const isCvcValid = (cvcError && cvcError.complete) || false;

    onCreditCardChange(isPanValid && isExpirationValid && isCvcValid);
  }, [panError, expirationError, cvcError]);

  return (
    <>
      {isScriptLoading && (
        <div className="flex justify-center w-full">
          <Loader size={36} />
        </div>
      )}
      <div style={{ display: isScriptLoading ? 'none' : 'block' }}>
        <div className="mb-24">
          <div className="flex justify-between mb-8">
            <label htmlFor="pan-field" className="self-end font-subtitle3">
              Número de tarjeta
            </label>
            <CardLogos />
          </div>
          <div
            id="pan-field"
            className="p-16 mt-8 mb-8 bg-white border border-solid border-gray-dark rounded-5 font-subtitle3"
          />
          {panError?.error && (
            <span className="font-caption text-red-main">
              {panError.error.message}
            </span>
          )}
        </div>
        <div className="flex w-full gap-24 mb-24">
          <div className="w-full mr-24">
            <label htmlFor="expiration-field" className="font-subtitle3">
              MM/AA
            </label>
            <div
              id="expiration-field"
              className="p-16 mt-8 mb-8 bg-white border border-solid border-gray-dark rounded-5 font-subtitle3"
            />
            {expirationError?.error && (
              <span className="font-caption text-red-main">
                {expirationError.error.message}
              </span>
            )}
          </div>
          <div className="w-full">
            <label htmlFor="cvv-field" className="font-subtitle3">
              CVC/CVV
            </label>
            <div
              id="cvv-field"
              className="p-16 mt-8 mb-8 bg-white border border-solid border-gray-dark rounded-5 font-subtitle3"
            />
            {cvcError?.error && (
              <span className="font-caption text-red-main">
                {cvcError.error.message}
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DLocal;
